import { useEffect } from "react";
import { Link, createFileRoute } from "@tanstack/react-router";
import { ArrowLeft, FileEdit, FileText, Plus, Users } from "lucide-react";
import { useTranslation } from "react-i18next";
import { RequireAuth } from "@/components/RequireAuth";
import { AppShell } from "@/components/layout/AppShell";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { PayslipCard } from "@/components/payroll/PayslipCard";
import { AdjustmentCard } from "@/components/payroll/AdjustmentCard";
import { usePayrollEmployees } from "@/lib/api/payroll-employees.queries";
import { usePayrollPayslips } from "@/lib/api/payroll-payslips.queries";
import { usePayrollAdjustments } from "@/lib/api/payroll-adjustments.queries";
import { useActiveCompany } from "@/lib/company-context";

export const Route = createFileRoute("/companies/$companyId/nomina/")({
  component: NominaOverviewPage,
});

function NominaOverviewPage() {
  return (
    <RequireAuth>
      <AppShell>
        <NominaOverviewContent />
      </AppShell>
    </RequireAuth>
  );
}

function NominaOverviewContent() {
  const { t } = useTranslation();
  const { companyId } = Route.useParams();
  const { setActiveCompanyId } = useActiveCompany();

  useEffect(() => {
    if (companyId) setActiveCompanyId(companyId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const employeesQ = usePayrollEmployees(companyId, { status: "active", pageSize: 200 });
  const payslipsQ = usePayrollPayslips(companyId, { pageSize: 6 });
  const adjustmentsQ = usePayrollAdjustments(companyId, { pageSize: 4 });

  const employeeCount = employeesQ.data?.pagination.total ?? 0;
  const payslips = payslipsQ.data?.payslips ?? [];
  const adjustments = adjustmentsQ.data?.adjustments ?? [];

  return (
    <div className="mx-auto flex max-w-6xl flex-col gap-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <Link
            to="/companies/$companyId"
            params={{ companyId }}
            className="inline-flex w-fit items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-3 w-3" />
            {t("payroll.overview.back")}
          </Link>
          <h1 className="mt-1 text-2xl font-semibold tracking-tight">
            {t("payroll.overview.title")}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {t("payroll.overview.subtitle")}
          </p>
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-3">
        <SummaryTile
          icon={<Users className="h-6 w-6 text-primary" />}
          label={t("payroll.overview.employees_label")}
          count={employeeCount}
          to="/companies/$companyId/nomina/empleados"
          companyId={companyId}
          ctaKey="payroll.overview.manage_employees"
        />
        <SummaryTile
          icon={<FileText className="h-6 w-6 text-primary" />}
          label={t("payroll.overview.payslips_label")}
          count={payslipsQ.data?.pagination.total ?? 0}
          to="/companies/$companyId/nomina/payslips"
          companyId={companyId}
          ctaKey="payroll.overview.manage_payslips"
        />
        <SummaryTile
          icon={<FileEdit className="h-6 w-6 text-primary" />}
          label={t("payroll.overview.adjustments_label")}
          count={adjustmentsQ.data?.pagination.total ?? 0}
          to="/companies/$companyId/nomina/ajustes"
          companyId={companyId}
          ctaKey="payroll.overview.manage_adjustments"
        />
      </div>

      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-4">
          <div>
            <CardTitle className="text-base">
              {t("payroll.overview.recent_payslips")}
            </CardTitle>
            <CardDescription className="text-xs">
              {t("payroll.overview.recent_payslips_subtitle")}
            </CardDescription>
          </div>
          <Button asChild size="sm">
            <Link
              to="/companies/$companyId/nomina/payslips/new"
              params={{ companyId }}
            >
              <Plus className="h-3.5 w-3.5" />
              {t("payroll.overview.new_payslip")}
            </Link>
          </Button>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {payslips.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {t("payroll.overview.empty_payslips")}
            </p>
          ) : (
            payslips.map((p) => (
              <PayslipCard key={p.id} companyId={companyId} payslip={p} />
            ))
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-4">
          <div>
            <CardTitle className="text-base">
              {t("payroll.overview.recent_adjustments")}
            </CardTitle>
          </div>
          <Button asChild size="sm" variant="outline">
            <Link
              to="/companies/$companyId/nomina/ajustes/new"
              params={{ companyId }}
            >
              <Plus className="h-3.5 w-3.5" />
              {t("payroll.overview.new_adjustment")}
            </Link>
          </Button>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2">
          {adjustments.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {t("payroll.overview.empty_adjustments")}
            </p>
          ) : (
            adjustments.map((a) => (
              <AdjustmentCard key={a.id} companyId={companyId} adjustment={a} />
            ))
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function SummaryTile({
  icon,
  label,
  count,
  to,
  companyId,
  ctaKey,
}: {
  icon: React.ReactNode;
  label: string;
  count: number;
  to:
    | "/companies/$companyId/nomina/empleados"
    | "/companies/$companyId/nomina/payslips"
    | "/companies/$companyId/nomina/ajustes";
  companyId: string;
  ctaKey: string;
}) {
  const { t } = useTranslation();
  return (
    <Card>
      <CardContent className="flex items-center gap-3 py-6">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
          {icon}
        </div>
        <div className="flex flex-col">
          <span className="text-2xl font-semibold">{count}</span>
          <span className="text-xs text-muted-foreground">{label}</span>
          <Link
            to={to}
            params={{ companyId }}
            className="mt-1 text-xs font-medium text-primary hover:underline"
          >
            {t(ctaKey)}
          </Link>
        </div>
      </CardContent>
    </Card>
  );
}
